import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import Field from "../components/Field";
import PrimaryButton from "../components/PrimaryButton";
import { toast } from "react-toastify";
import { forgotPassword } from "../api/authService";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();

    if (!email) {
      toast.warn("Please enter your email address", {
        position: "top-center",
        autoClose: 2000
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address", {
        position: "top-center",
        autoClose: 2000
      });
      return;
    }

    setLoading(true);
    try {
      const data = await forgotPassword(email);
      toast.dismiss();
      toast.success(data.message || "Password reset instructions sent to your email", {
        position: "top-center",
        autoClose: 4000
      });
      setEmailSent(true);

      if (data.devResetLink) {
        console.log('Development reset link:', data.devResetLink);
        console.log('Development token:', data.devToken);
        toast.info('Check console for development reset link', {
          position: "bottom-right",
          autoClose: 8000
        });
      }

      setTimeout(() => {
        navigate("/login");
      }, 5000);
    } catch (err) {
      toast.dismiss();
      console.error('Forgot password error:', err);

      if (err.response?.status === 404) {
        toast.error(err.response.data?.message || "This email does not exist in our system", {
          position: "top-center",
          autoClose: 4000
        });
      } else if (err.response?.status === 400) {
        toast.error(err.response.data?.message || "Invalid request. Please check your email.", {
          position: "top-center",
          autoClose: 3000
        });
      } else if (err.response?.status === 500) {
        toast.error(err.response.data?.message || "Failed to send reset email. Please contact support.", {
          position: "top-center",
          autoClose: 4000
        });
      } else {
        const errorMessage = err.response?.data?.message || "Failed to send reset email. Please try again.";
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-emerald-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors duration-300" />
      <div className="pointer-events-none absolute inset-0 opacity-[.04] dark:opacity-[.08] [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22><path fill=%22%23a1a1aa%22 fill-opacity=%220.5%22 d=%22M0 39h40v1H0zM39 0v40h1V0z%22/></svg>')]" />

      {}
      <div className="relative w-full max-w-md px-4 sm:px-6">
        {}
        <div className="flex items-center justify-center mb-8">
          <Logo />
        </div>

        {}
        <form
          onSubmit={submit}
          className="w-full bg-white/80 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl
                     ring-1 ring-black/5 dark:ring-white/10 shadow-[0_20px_60px_-20px_rgba(2,6,23,0.25)] dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]
                     p-6 sm:p-8 md:p-10 space-y-6 sm:space-y-8 transition-colors"
          aria-label="Forgot password form"
        >
          <header className="space-y-2 text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Forgot Password?
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
              {emailSent
                ? "Check your email for reset instructions"
                : "Enter your email address and we'll send you instructions to reset your password"
              }
            </p>
          </header>

          {!emailSent && (
            <>
              <div className="space-y-4">
                <Field
                  placeholder="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <PrimaryButton
                type="submit"
                disabled={loading}
                aria-label="Send reset instructions"
              >
                {loading ? "Sending..." : "Send Reset Instructions"}
              </PrimaryButton>
            </>
          )}

          {emailSent && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-center">
              <div className="text-emerald-700 dark:text-emerald-300 font-medium mb-2">Email Sent Successfully</div>
              <div className="text-sm text-emerald-600 dark:text-emerald-400">
                Please check your inbox and follow the instructions to reset your password.
              </div>
              <div className="text-xs text-emerald-500 dark:text-emerald-500 mt-2">
                Redirecting to login...
              </div>
            </div>
          )}

          <div className="text-sm text-center">
            <Link to="/login" className="text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors">
              Back to Login
            </Link>
          </div>
        </form>

        {}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500 dark:text-slate-500">
            If you don't receive an email within a few minutes, please check your spam folder or contact support.
          </p>
        </div>
      </div>
    </div>
  );
}
